import { LitElement, html, css, nothing } from 'lit';
import { PRINT_STYLES } from './print-styles.js';
import { repeat } from 'lit/directives/repeat.js';
import { StoreMixin, store } from '../store.js';
import { api } from '../api.js';
import { navigate } from '../router.js';
import { apiParams } from '../lib/filters.js';
import { CONFIDENCE_TIERS } from '../lib/palette.js';
import { fillForTier } from '../lib/patterns.js';
import {
  EXCLUSION_WORDS,
  PROJECT_PARAM_KEYS,
  SIMILARITY_COMPONENTS,
  analogExportRows,
  assignmentExportRows,
  bandSentence,
  burnSentence,
  burnState,
  curveScaleMax,
  dealSentence,
  flagLines,
  foldRoles,
  recurringSentence,
  remainingSentence,
  roleExportRows,
  scheduleSentence,
  slipSentence,
  sourceMeta,
  sourceSentence,
  usedForWords,
  weekExportRows,
  weekStack,
  projectAnalogColumns,
  projectAssignmentColumns,
  projectRoleColumns,
  projectWeekColumns,
} from '../lib/project.js';
import { withFormats } from '../lib/table.js';
import {
  hours as formatHours,
  percent,
  count,
  weekLabel,
  mediumDate,
  longDate,
  humanize,
} from '../lib/format.js';

const fmt = { hours: formatHours, percent, count, mediumDate, week: weekLabel };
const exact = (h) => formatHours(h, { exact: true });
const TIER_META = new Map(CONFIDENCE_TIERS.map((t) => [t.key, t]));

/**
 * The Project Intelligence panel (PRD 10.5; Phase 31): where one project's forecast numbers came
 * from. One response, `GET /forecast/projects/:id`, said top to bottom in the sketch's order —
 * the source mode with its tier and observed error band, the remaining hours against the plan
 * with the burn reading, the schedule and any slip, the curve the hours were spread on, the role
 * distribution applied, the analogs and what each lent, the assumption flags, the linked deals —
 * with the recurring baseline in place of the plan for a retainer, and four tables beneath.
 *
 * The card adds no arithmetic: every figure is a field of the response, and the words for the
 * fields live in `lib/project.js`, where the tests can hold them. The curve is drawn as keyed
 * groups per role so a run change updates the survivors in place (PRD 7.3); the roles wear the
 * type hue slots by share, as the bench does, because no project type is drawn on this page.
 */
class ProjectIntelligence extends StoreMixin(LitElement) {
  static properties = {
    projectId: { type: String, attribute: 'project-id' },
    _data: { state: true },
    _loading: { state: true },
    _error: { state: true },
    _notFound: { state: true },
    _hover: { state: true },
    _weeksOpen: { state: true },
    _rolesOpen: { state: true },
    _analogsOpen: { state: true },
    _assignmentsOpen: { state: true },
  };

  constructor() {
    super();
    this.projectId = null;
    this._data = null;
    this._loading = false;
    this._error = null;
    this._notFound = null;
    this._hover = null;
    this._weeksOpen = false;
    this._rolesOpen = false;
    this._analogsOpen = false;
    this._assignmentsOpen = false;
    this._requestKey = null;
    this._fold = null;
    this._foldFor = null;
  }

  static styles = [
    PRINT_STYLES,
    css`
      :host {
        display: block;
      }
      .card {
        background: var(--surface);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-card);
        box-shadow: var(--shadow-card);
        padding: 20px 24px 18px;
      }
      .head {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px 18px;
        margin-bottom: 14px;
      }
      .eyebrow {
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      h2 {
        margin: 2px 0 0;
        font-size: 20px;
        line-height: 1.2;
        color: var(--ink-strong);
      }
      h2 .number {
        font-family: var(--font-eyebrow);
        font-size: 13px;
        font-weight: 600;
        color: var(--ink-muted);
        margin-right: 8px;
        letter-spacing: 0.04em;
      }
      h2 a.number {
        color: var(--link);
        text-decoration: underline;
        text-underline-offset: 2px;
      }
      .sub {
        color: var(--ink-muted);
        font-size: 12.5px;
      }
      .kpis {
        margin-left: auto;
        display: flex;
        gap: 18px;
        flex-wrap: wrap;
      }
      .kpi {
        min-width: 88px;
      }
      .kpi .label {
        font-family: var(--font-eyebrow);
        font-size: 9.5px;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      .kpi .value {
        font-size: 16px;
        font-weight: 600;
        color: var(--ink-strong);
        font-variant-numeric: tabular-nums;
      }
      .kpi .value small {
        font-size: 11.5px;
        font-weight: 500;
        color: var(--ink-muted);
      }
      .grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
        gap: 16px 24px;
        margin: 6px 0 14px;
      }
      .block h3 {
        margin: 0 0 6px;
        font-family: var(--font-eyebrow);
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--ink-muted);
      }
      .block p {
        margin: 0 0 4px;
        font-size: 13px;
        line-height: 1.45;
        color: var(--ink);
      }
      .block p.muted {
        color: var(--ink-muted);
        font-size: 12.5px;
      }
      .chip {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 11.5px;
        font-weight: 600;
        color: var(--ink-strong);
        background: var(--surface-2);
        border: 1px solid var(--border-soft);
        border-radius: var(--radius-pill);
        padding: 1px 8px;
        vertical-align: middle;
      }
      .chip .marker {
        font-family: var(--font-eyebrow);
        color: var(--ink-muted);
      }
      .chip.fast {
        color: var(--status-overloaded-ink);
        background: var(--status-overloaded-tint);
        border-color: transparent;
      }
      .chip.slow {
        color: var(--status-underload-ink);
        background: var(--status-underload-tint);
        border-color: transparent;
      }
      .meters {
        display: grid;
        grid-template-columns: max-content minmax(0, 1fr) max-content;
        gap: 4px 10px;
        align-items: center;
        margin: 8px 0 6px;
        font-size: 12px;
        color: var(--ink-muted);
      }
      .meters .track {
        height: 8px;
        background: var(--surface-sunken);
        border-radius: 4px;
        overflow: hidden;
      }
      .meters .track span {
        display: block;
        height: 100%;
        background: var(--bar);
        border-radius: 4px;
      }
      .meters .track span.hours {
        background: var(--ink-muted);
      }
      .meters .figure {
        font-variant-numeric: tabular-nums;
        color: var(--ink);
      }
      .roles-list,
      .analog-list,
      .flag-list,
      .deal-list,
      .plain-list {
        list-style: none;
        margin: 0;
        padding: 0;
      }
      .roles-list li {
        display: grid;
        grid-template-columns: minmax(0, 1fr) 44px 64px;
        gap: 0 10px;
        align-items: center;
        padding: 3px 0;
        font-size: 12.5px;
      }
      .roles-list .name {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;
      }
      .roles-list .name .label {
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .roles-list .name .bar {
        flex: 1 1 auto;
        height: 8px;
        border-radius: 4px;
        min-width: 2px;
      }
      .roles-list .share,
      .roles-list .hours {
        text-align: right;
        font-variant-numeric: tabular-nums;
      }
      .roles-list .hours {
        color: var(--ink-muted);
      }
      .analog-list li {
        display: grid;
        grid-template-columns: 22px minmax(0, 1fr) 90px;
        gap: 2px 10px;
        align-items: baseline;
        padding: 5px 0;
        border-top: 1px solid var(--border-soft);
        font-size: 12.5px;
      }
      .analog-list li:first-child {
        border-top: 0;
      }
      .analog-list .rank {
        font-family: var(--font-eyebrow);
        font-size: 11px;
        color: var(--ink-muted);
      }
      .analog-list .what b {
        font-weight: 600;
        color: var(--ink-strong);
      }
      .analog-list .what .detail {
        display: block;
        color: var(--ink-muted);
        font-size: 12px;
      }
      .analog-list .sim {
        text-align: right;
        font-variant-numeric: tabular-nums;
        font-weight: 600;
        color: var(--ink-strong);
      }
      .analog-list .sim small {
        display: block;
        font-weight: 500;
        color: var(--ink-muted);
        font-size: 11px;
      }
      .sim-bar {
        height: 4px;
        background: var(--surface-sunken);
        border-radius: 2px;
        margin-top: 3px;
        overflow: hidden;
      }
      .sim-bar span {
        display: block;
        height: 100%;
        background: var(--ink-muted);
      }
      .flag-list li,
      .deal-list li,
      .plain-list li {
        padding: 4px 0;
        font-size: 12.5px;
        border-top: 1px solid var(--border-soft);
      }
      .flag-list li:first-child,
      .deal-list li:first-child,
      .plain-list li:first-child {
        border-top: 0;
      }
      .flag-list b,
      .deal-list b {
        font-weight: 600;
        color: var(--ink-strong);
      }
      .flag-list .sentence,
      .deal-list .sentence {
        color: var(--ink-muted);
      }
      .deal-list a,
      a.link,
      button.link {
        color: var(--link);
        text-decoration: none;
        font: inherit;
        background: none;
        border: 0;
        padding: 0;
        cursor: pointer;
      }
      .deal-list a:hover,
      a.link:hover,
      button.link:hover {
        text-decoration: underline;
      }
      .curve {
        margin: 4px 0 10px;
      }
      .legend-row {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px 18px;
        margin-top: 6px;
      }
      .state {
        padding: 28px 0;
        color: var(--ink-muted);
        font-size: 13px;
      }
      .state.error {
        color: var(--status-overloaded-ink);
      }
      .note {
        margin: 6px 0 0;
        font-size: 12px;
        color: var(--ink-muted);
      }
      details.table {
        margin-top: 12px;
        border-top: 1px solid var(--border-soft);
        padding-top: 8px;
      }
      details.table summary {
        cursor: pointer;
        font-size: 12.5px;
        font-weight: 600;
        color: var(--ink-muted);
        list-style: none;
      }
      details.table summary::-webkit-details-marker {
        display: none;
      }
      details.table summary::before {
        content: '▸ ';
      }
      details.table[open] summary::before {
        content: '▾ ';
      }
      details.table data-table {
        margin-top: 10px;
      }
    `,
  ];

  connectedCallback() {
    super.connectedCallback();
    this._unsubscribeFetch = store.subscribe(() => this._maybeFetch());
    this._maybeFetch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeFetch?.();
  }

  updated(changed) {
    if (changed.has('projectId')) this._maybeFetch();
  }

  /** The request this panel wants: the project, for the run in the filter bar. */
  _params() {
    const { filters, run } = this.appState;
    if (!run || !this.projectId) return null;
    return { id: this.projectId, ...apiParams(filters, PROJECT_PARAM_KEYS) };
  }

  async _maybeFetch() {
    const params = this._params();
    const key = params ? JSON.stringify(params) : null;
    if (key === this._requestKey) return;
    this._requestKey = key;
    if (!params) {
      this._data = null;
      return;
    }
    const { id, ...query } = params;
    this._loading = true;
    this._error = null;
    this._notFound = null;
    try {
      const data = await api.get(`/forecast/projects/${id}`, query);
      if (this._requestKey !== key) return;
      this._data = data;
      this.dispatchEvent(new CustomEvent('project-data', { detail: data, bubbles: true }));
    } catch (err) {
      if (this._requestKey !== key) return;
      if (err.status === 404) this._notFound = err.message;
      else this._error = err.message;
      this._data = null;
    } finally {
      if (this._requestKey === key) this._loading = false;
    }
  }

  // -------------------------------------------------------------------------------------------
  // Model

  get _foldOf() {
    if (this._foldFor !== this._data) {
      this._fold = foldRoles(this._data?.roles?.rows ?? []);
      this._foldFor = this._data;
    }
    return this._fold;
  }

  get _segments() {
    const fold = this._foldOf;
    return [...fold.drawn, ...(fold.other ? [fold.other] : [])];
  }

  _stackOf(week) {
    return weekStack(week, this._foldOf);
  }

  _tokenOf(service) {
    const fold = this._foldOf;
    return fold.drawn.find((r) => r.key === service)?.token ?? fold.other?.token ?? null;
  }

  // -------------------------------------------------------------------------------------------
  // Drawing

  _marks = ({ x, y, plot, svg: s }) => {
    const d = this._data;
    const weeks = d?.weeks ?? [];
    const tier = d?.project?.confidence_tier;
    const fill = fillForTier(tier);
    const stacks = new Map(weeks.map((w) => [w.week_start, this._stackOf(w)]));
    const segments = this._segments;
    const paint = (token) =>
      fill === 'solid' ? `var(${token})` : `url(#waag-${fill}-${String(token).replace(/^--/, '')})`;
    return s`
      <g class="roles">
        ${repeat(
          segments,
          (seg) => seg.key,
          (seg, i) => s`
            <g class="role" data-role=${seg.key}>
              ${weeks.map((w) => {
                const part = stacks.get(w.week_start)[i];
                if (!part || part.hours <= 0) return nothing;
                return s`<rect class="seg" data-week=${w.week_start}
                              x=${x(w.week_start)} width=${x.bandwidth()}
                              y=${y(part.to)} height=${Math.max(0, y(part.from) - y(part.to))}
                              fill=${paint(seg.token)} stroke="var(--surface)" stroke-width="1" />`;
              })}
            </g>`,
        )}
      </g>
      ${
        this._hover !== null && weeks[this._hover]
          ? s`<line class="crosshair" x1=${x.centre(weeks[this._hover].week_start)}
                    x2=${x.centre(weeks[this._hover].week_start)} y1=${plot.top} y2=${plot.top + plot.height}
                    stroke="var(--ink-muted)" stroke-width="1" stroke-dasharray="2 2" pointer-events="none" />`
          : nothing
      }
      <g class="hits">
        ${weeks.map(
          (w, i) => s`
            <rect class="hit" x=${x(w.week_start) - (x.step() - x.bandwidth()) / 2} width=${x.step()}
                  y=${plot.top} height=${plot.height} fill="transparent" style="cursor: crosshair"
                  @pointermove=${(e) => this._showTooltip(e, i)}
                  @pointerleave=${() => this._hideTooltip()}
                  @focus=${(e) => this._showTooltip(e, i)}
                  @blur=${() => this._hideTooltip()}
                  tabindex="0" role="img"
                  aria-label=${`Week of ${weekLabel(w.week_start)}: ${formatHours(w.hours)}`} />`,
        )}
      </g>
    `;
  };

  _showTooltip(event, index) {
    const w = this._data?.weeks?.[index];
    if (!w) return;
    this._hover = index;
    const tooltip = this.renderRoot.querySelector('chart-tooltip');
    if (!tooltip) return;
    const rect = event.currentTarget?.getBoundingClientRect?.();
    const rows = [...this._stackOf(w)]
      .reverse()
      .filter((seg) => seg.hours > 0)
      .map((seg) => ({
        label: seg.roles ? `${seg.label} (${count(seg.roles.length)})` : seg.label,
        value: exact(seg.hours),
        token: seg.token,
        fill: 'solid',
      }));
    rows.push({ label: 'Total', value: exact(w.hours) });
    const tier = this._data.project.confidence_tier;
    tooltip.show({
      x: event.clientX ?? (rect ? rect.left + rect.width / 2 : 0),
      y: event.clientY ?? (rect ? rect.top : 0),
      title: `Week of ${longDate(w.week_start)}`,
      rows,
      footer: `${sourceMeta(this._data.project.source_mode).label} · ${TIER_META.get(tier)?.label ?? humanize(tier)} confidence`,
    });
  }

  _hideTooltip() {
    this._hover = null;
    this.renderRoot.querySelector('chart-tooltip')?.hide();
  }

  // -------------------------------------------------------------------------------------------
  // Blocks

  _tierChip(tier) {
    const meta = TIER_META.get(tier);
    if (!meta) return nothing;
    return html`<span class="chip" data-tier=${tier}
      >${meta.marker ? html`<span class="marker">${meta.marker}</span>` : nothing} ${meta.label}
      confidence</span
    >`;
  }

  _sourceBlock(d) {
    const meta = sourceMeta(d.project.source_mode);
    const band = bandSentence(d.confidence_band, fmt);
    return html`
      <section class="block" data-block="source">
        <h3>Source mode</h3>
        <p data-field="source"><b>${meta.label}</b> ${this._tierChip(d.project.confidence_tier)}</p>
        <p class="muted">${meta.sentence}</p>
        <p class="muted" data-field="band">
          ${
            band ??
            'No observed error band yet: the accuracy job publishes one once settled weeks exist.'
          }
        </p>
      </section>
    `;
  }

  _remainingBlock(d) {
    const r = d.remaining;
    const recurring = d.project.source_mode === 'recurring_baseline';
    const burn = burnState(r);
    return html`
      <section class="block" data-block="remaining">
        <h3>${recurring ? 'Baseline' : 'Remaining'}</h3>
        <p data-field="remaining"><b>${remainingSentence(d, fmt)}</b></p>
        ${
          recurring
            ? html`<p class="muted" data-field="recurring">
                ${recurringSentence(d.project.recurring, fmt)}
              </p>`
            : html`
                <div class="meters" role="img" aria-label="Schedule elapsed against hours consumed">
                  <span>Schedule</span>
                  <span class="track"
                    ><span
                      style=${`width:${Math.min(100, Math.round((r.schedule_elapsed_fraction ?? 0) * 100))}%`}
                    ></span
                  ></span>
                  <span class="figure">${percent(r.schedule_elapsed_fraction)}</span>
                  <span>Hours</span>
                  <span class="track"
                    ><span
                      class="hours"
                      style=${`width:${Math.min(100, Math.round((r.hours_consumed_fraction ?? 0) * 100))}%`}
                    ></span
                  ></span>
                  <span class="figure">${percent(r.hours_consumed_fraction)}</span>
                </div>
                <p data-field="burn">
                  <span class=${`chip ${burn.key}`} data-burn=${burn.key}
                    >${burn.marker ? html`<span class="marker">${burn.marker}</span>` : nothing}
                    ${burn.label}</span
                  >
                  <span class="muted"> ${burnSentence(r, fmt)}</span>
                </p>
                ${
                  r.hours_task_complete > 0 || r.hours_unscheduled > 0 || r.hours_beyond_horizon > 0
                    ? html`<p class="muted" data-field="not-drawn">
                        Not in the chart:
                        ${[
                          r.hours_task_complete > 0
                            ? `${formatHours(r.hours_task_complete)} on complete tasks`
                            : null,
                          r.hours_unscheduled > 0
                            ? `${formatHours(r.hours_unscheduled)} with no window`
                            : null,
                          r.hours_beyond_horizon > 0
                            ? `${formatHours(r.hours_beyond_horizon)} beyond the horizon`
                            : null,
                        ]
                          .filter(Boolean)
                          .join(', ')}.
                      </p>`
                    : nothing
                }
              `
        }
      </section>
    `;
  }

  _scheduleBlock(d) {
    const slip = slipSentence(d.schedule, fmt);
    const s = d.schedule;
    return html`
      <section class="block" data-block="schedule">
        <h3>Schedule</h3>
        <p data-field="schedule">${scheduleSentence(s, fmt)}</p>
        ${slip ? html`<p data-field="slip"><b>Slipped.</b> <span class="muted">${slip}</span></p>` : nothing}
        ${
          s.first_week
            ? html`<p class="muted" data-field="drawn">
                Hours in this run from the week of ${mediumDate(s.first_week)} to the week of
                ${mediumDate(s.last_week)}, ${count(s.weeks_with_hours)}
                ${s.weeks_with_hours === 1 ? 'week' : 'weeks'} with hours.
              </p>`
            : html`<p class="muted" data-field="drawn">No hours in the horizon.</p>`
        }
      </section>
    `;
  }

  _rolesBlock(d) {
    const rows = d.roles.rows;
    const source = d.roles.source;
    const words = {
      assignments: 'the split of the allocated hours by role',
      analogs: 'the analogs’ mix, weighted by similarity',
      canonical_type: 'the pooled mix of closed projects of the type',
      all_closed: 'the pooled mix of every closed project',
      baseline: 'the baseline per service',
    };
    return html`
      <section class="block" data-block="roles">
        <h3>Role distribution applied</h3>
        ${
          rows.length
            ? html`<ul class="roles-list">
                ${repeat(
                  rows,
                  (r) => r.canonical_service,
                  (r) => html`
                    <li data-role=${r.canonical_service}>
                      <span class="name">
                        <span class="label" title=${r.label}>${r.label}</span>
                        <span
                          class="bar"
                          style=${`width:${Math.max(2, Math.round(r.share * 100))}%; background: var(${this._tokenOf(r.canonical_service)})`}
                        ></span>
                      </span>
                      <span class="share">${percent(r.share)}</span>
                      <span class="hours"
                        >${
                          r.weekly_hours !== null && r.weekly_hours !== undefined
                            ? `${formatHours(r.weekly_hours, { unit: false })} h/wk`
                            : formatHours(r.hours)
                        }</span
                      >
                    </li>
                  `,
                )}
              </ul>`
            : html`<p class="muted">No roles: the run put no hours on this project.</p>`
        }
        <p class="muted" data-field="roles-source">
          ${source ? `Shares are ${words[source] ?? source}.` : ''}
          ${d.roles.total_hours > 0 ? ` ${formatHours(d.roles.total_hours)} in the horizon.` : ''}
        </p>
      </section>
    `;
  }

  _analogsBlock(d) {
    const analogs = d.analogs;
    return html`
      <section class="block" data-block="analogs">
        <h3>Similar projects used</h3>
        ${
          analogs.length
            ? html`<ul class="analog-list">
                ${repeat(
                  analogs,
                  (a) => a.project_id,
                  (a) => html`
                    <li data-analog=${a.project_id}>
                      <span class="rank">${a.rank}</span>
                      <span class="what">
                        <b>${a.source_project_number ?? ''} ${a.name}</b>
                        <span class="detail">
                          ${a.client ?? '—'}${a.same_client ? ' (same client)' : ''} ·
                          ${formatHours(a.hours_actual)} of ${formatHours(a.hours_planned)}
                          ${a.overrun_ratio !== null ? `(×${Number(a.overrun_ratio).toFixed(2)})` : ''}
                          · ${usedForWords(a)}
                        </span>
                        <span class="sim-bar" aria-hidden="true"
                          ><span style=${`width:${Math.round((a.similarity ?? 0) * 100)}%`}></span
                        ></span>
                      </span>
                      <span
                        class="sim"
                        title=${SIMILARITY_COMPONENTS.map(
                          ([k, label]) => `${label}: ${a.components?.[k] ?? '—'}`,
                        ).join('\n')}
                      >
                        ${Number(a.similarity).toFixed(2)}
                        <small>${a.completed_on ? `done ${mediumDate(a.completed_on)}` : ''}</small>
                      </span>
                    </li>
                  `,
                )}
              </ul>`
            : html`<p class="muted" data-field="no-analogs">
                ${
                  d.project.source_mode === 'recurring_baseline'
                    ? 'A retainer borrows nothing: the baseline is its own history.'
                    : 'No analogs matched: no closed project of this type scored above the threshold.'
                }
              </p>`
        }
      </section>
    `;
  }

  _flagsBlock(d) {
    const lines = flagLines(d.flags);
    return html`
      <section class="block" data-block="flags">
        <h3>Assumption flags</h3>
        ${
          lines.length
            ? html`<ul class="flag-list">
                ${lines.map(
                  (f) => html`
                    <li data-flag=${f.flag}>
                      <b>${f.label}</b>
                      ${f.sentence ? html`<span class="sentence"> — ${f.sentence}</span>` : nothing}
                    </li>
                  `,
                )}
              </ul>`
            : html`<p class="muted" data-field="no-flags">
                None: the run took nothing on assumption.
              </p>`
        }
      </section>
    `;
  }

  _dealsBlock(d) {
    const deals = d.linked_deals;
    return html`
      <section class="block" data-block="deals">
        <h3>Linked HubSpot ${deals.length === 1 ? 'deal' : 'deals'}</h3>
        ${
          deals.length
            ? html`<ul class="deal-list">
                ${deals.map(
                  (deal) => html`
                    <li data-deal=${deal.source_deal_id}>
                      ${
                        deal.source_url
                          ? html`<a
                              href=${deal.source_url}
                              target="_blank"
                              rel="noreferrer noopener"
                              >${dealSentence(deal)}</a
                            >`
                          : html`<b>${dealSentence(deal)}</b>`
                      }
                      <span class="sentence">
                        ·
                        ${deal.stage_name ?? humanize(deal.status)}${
                          deal.amount !== null ? ` · $${count(deal.amount)}` : ''
                        }
                      </span>
                    </li>
                  `,
                )}
              </ul>`
            : html`<p class="muted" data-field="no-deal">
                No deal linked.
                ${
                  d.deal_source_id
                    ? `Workamajig records ${d.deal_source_id} on the project, but no such deal has synced.`
                    : ''
                }
              </p>`
        }
        ${
          d.deal_source_id && deals.some((x) => x.source_deal_id === d.deal_source_id)
            ? html`<p class="muted" data-field="deal-id">
                ${d.deal_source_id} is the deal id recorded on the project in Workamajig.
              </p>`
            : nothing
        }
      </section>
    `;
  }

  _accuracyBlock(d) {
    const acc = d.accuracy;
    if (!acc || !acc.weeks?.length) return nothing;
    return html`
      <section class="block" data-block="accuracy">
        <h3>Measured so far</h3>
        <p>
          ${count(acc.weeks.length)} settled ${acc.weeks.length === 1 ? 'week' : 'weeks'} of this
          project's forecast have been checked against the time logged: mean error
          ${formatHours(acc.mae)}, bias
          ${acc.bias !== null && acc.bias > 0 ? '+' : ''}${formatHours(acc.bias)}.
        </p>
      </section>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Tables

  get _weekColumns() {
    // The specification is shared with the server's CSV (lib/project.js); the drawing is here.
    return withFormats(projectWeekColumns({ roles: this._data?.roles?.rows ?? [] }), {
      byUnit: { hours: (v) => exact(v), week: (v) => weekLabel(v) },
    });
  }

  get _roleColumns() {
    return withFormats(projectRoleColumns(), {
      byUnit: { hours: (v) => exact(v), percent: (v) => percent(v) },
    });
  }

  get _analogColumns() {
    const score = (v) => (v === null || v === undefined ? '—' : Number(v).toFixed(2));
    return withFormats(projectAnalogColumns(), {
      byUnit: { hours: (v) => exact(v), number: score, date: (v) => mediumDate(v) },
      byKey: {
        similarity: (v) => Number(v).toFixed(2),
        same_client: (v) => (v ? 'yes' : 'no'),
        overrun_ratio: (v) => (v === null ? '—' : `×${Number(v).toFixed(2)}`),
      },
    });
  }

  get _assignmentColumns() {
    return withFormats(projectAssignmentColumns(), {
      byUnit: { hours: (v) => exact(v), date: (v) => mediumDate(v) },
      byKey: { slipped: (v) => (v ? 'yes' : 'no') },
    });
  }

  _table(name, open, onToggle, caption, exportName, columns, rows, pageSize = 25) {
    return html`
      <details class="table" ?open=${open} @toggle=${onToggle}>
        <summary>Table view: ${name}</summary>
        <data-table
          caption=${caption}
          export-name=${exportName}
          page-size=${pageSize}
          dense
          .columns=${columns}
          .rows=${rows}
        ></data-table>
      </details>
    `;
  }

  // -------------------------------------------------------------------------------------------
  // Render

  _subtitle(d) {
    const p = d.project;
    const parts = [p.client, p.canonical_type ? humanize(p.canonical_type) : 'Unmapped type'];
    if (d.record?.status) parts.push(d.record.status);
    if (!p.included)
      parts.push(`excluded: ${EXCLUSION_WORDS[p.exclusion_reason] ?? p.exclusion_reason}`);
    return parts.filter(Boolean).join(' · ');
  }

  render() {
    const d = this._data;
    const { run } = this.appState;
    // The number opens the project in Workamajig once `quality.project_url_template` is set.
    const number = d?.project.source_project_number ?? '';
    const sourceUrl = d?.record?.source_url;
    const title = d
      ? html`${
          sourceUrl
            ? html`<a
                class="number"
                href=${sourceUrl}
                target="_blank"
                rel="noreferrer noopener"
                title="Open ${number || d.project.name} in Workamajig"
                >${number || 'Workamajig'}</a
              >`
            : html`<span class="number">${number}</span>`
        }
        ${d.project.name}`
      : 'Project intelligence';

    let body;
    if (!run) body = html`<div class="state">No forecast run yet — nothing to explain.</div>`;
    else if (!this.projectId)
      body = html`<div class="state">Pick a project to see where its numbers came from.</div>`;
    else if (this._notFound)
      body = html`<div class="state" data-state="not-found">
        This project is not in the run of ${mediumDate(run.planning_date)}: it may have closed, or
        the run may predate it. Choose another run in the filter bar, or
        <button class="link" @click=${() => navigate('/projects')}>pick another project</button>.
      </div>`;
    else if (this._error) body = html`<div class="state error">${this._error}</div>`;
    else if (!d && this._loading) body = html`<div class="state">Reading the project…</div>`;
    else if (!d) body = html`<div class="state">Nothing to show.</div>`;
    else {
      const weeks = d.weeks;
      body = html`
        <div class="grid">
          ${this._sourceBlock(d)} ${this._remainingBlock(d)} ${this._scheduleBlock(d)}
        </div>
        ${
          weeks.length
            ? html`
                <div class="curve">
                  <chart-frame
                    .categories=${weeks.map((w) => w.week_start)}
                    .max=${curveScaleMax(weeks)}
                    .marks=${this._marks}
                    .formatCategory=${(c) => weekLabel(c)}
                    value-label="Hours"
                    category-label="Week commencing"
                    height="240"
                  ></chart-frame>
                  <div class="legend-row">
                    <chart-legend
                      .items=${[...this._segments].reverse().map((seg) => ({
                        label: seg.roles ? `${seg.label} (${count(seg.roles.length)})` : seg.label,
                        token: seg.token,
                        fill: fillForTier(d.project.confidence_tier),
                      }))}
                    ></chart-legend>
                  </div>
                </div>
              `
            : html`<p class="note" data-note="no-weeks">
                No hours in the horizon: nothing to draw.
              </p>`
        }
        <div class="grid">
          ${this._rolesBlock(d)} ${this._analogsBlock(d)} ${this._flagsBlock(d)}
          ${this._dealsBlock(d)} ${this._accuracyBlock(d)}
        </div>
        ${
          d.withheld_count > 0
            ? html`<p class="note" data-note="withheld">
                ${count(d.withheld_count)} assignment rows are on people outside your scope; their
                hours are in the totals and the roles, and their names are not here.
              </p>`
            : nothing
        }
        ${this._table(
          'weeks',
          this._weeksOpen,
          (e) => (this._weeksOpen = e.target.open),
          `Hours per week by role — ${d.project.name}`,
          'project-weeks',
          this._weekColumns,
          weekExportRows(d),
          26,
        )}
        ${this._table(
          'roles',
          this._rolesOpen,
          (e) => (this._rolesOpen = e.target.open),
          `The role distribution applied — ${d.project.name}`,
          'project-roles',
          this._roleColumns,
          roleExportRows(d),
        )}
        ${this._table(
          'analogs',
          this._analogsOpen,
          (e) => (this._analogsOpen = e.target.open),
          `The analogs matched, with their similarity components — ${d.project.name}`,
          'project-analogs',
          this._analogColumns,
          analogExportRows(d),
        )}
        ${this._table(
          d.project.source_mode === 'recurring_baseline'
            ? 'baseline units'
            : d.project.source_mode === 'inferred_role_mix'
              ? 'inferred roles'
              : 'assignments',
          this._assignmentsOpen,
          (e) => (this._assignmentsOpen = e.target.open),
          `Every row the run wrote for this project, grouped — ${d.project.name}`,
          'project-assignments',
          this._assignmentColumns,
          assignmentExportRows(d),
          50,
        )}
      `;
    }

    return html`
      <div class="card">
        <div class="head">
          <div>
            <div class="eyebrow">
              Project intelligence${run ? ` · run of ${mediumDate(run.planning_date)}` : ''}
            </div>
            <h2>${title}</h2>
            ${d ? html`<div class="sub">${this._subtitle(d)}</div>` : nothing}
          </div>
          ${
            d
              ? html`<div class="kpis">
                  <div class="kpi" data-kpi="source">
                    <div class="label">Source</div>
                    <div class="value">${sourceMeta(d.project.source_mode).label}</div>
                  </div>
                  <div class="kpi" data-kpi="remaining">
                    <div class="label">Remaining</div>
                    <div class="value">
                      ${formatHours(d.remaining.hours_remaining_adjusted)}
                      ${
                        d.remaining.hours_remaining_adjusted !== d.remaining.hours_remaining
                          ? html`<small
                              >(${formatHours(d.remaining.hours_remaining)} before inflation)</small
                            >`
                          : nothing
                      }
                    </div>
                  </div>
                  <div class="kpi" data-kpi="horizon">
                    <div class="label">In horizon</div>
                    <div class="value">${formatHours(d.remaining.hours_allocated)}</div>
                  </div>
                  <div class="kpi" data-kpi="analogs">
                    <div class="label">Analogs</div>
                    <div class="value">${count(d.analogs.length)}</div>
                  </div>
                </div>`
              : nothing
          }
        </div>
        ${d ? html`<p class="sub" data-field="source-line">${sourceSentence(d, fmt)}</p>` : nothing}
        ${body}
        <chart-tooltip></chart-tooltip>
      </div>
    `;
  }
}

customElements.define('project-intelligence', ProjectIntelligence);
